/**
 * WetSignCompletion — QR-scan-driven wet-sign reassembly.
 *
 * Calls `apiClient.signing.completeWetSign(...)`. Critical:
 *   - Success: PascalCase envelope `{Success, CartId, MarkedAt, ...}`.
 *   - 422 failure: camelCase envelope `{success:false, reason, error}`.
 *
 * The api-client throws `ApiError` on 422 with `code = reason` and
 * `details` carrying the failure body. We narrow on `ApiError.isApiError(err)`
 * and surface `err.code` + `err.message`.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, CheckCircle2, FileSignature, AlertTriangle } from '@retail-ops/panel-shell/fi';
import { Button, Pill, WidgetCard } from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { ApiError } from '@retail-ops/api-client';
import { formatLocal } from '@/utils/time';
import './rsc.css';

export function WetSignCompletion(): JSX.Element {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const apiClient = useApiClient();
  const { user } = useAuth();

  const [cartId, setCartId] = useState('');
  const [qrPayload, setQrPayload] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerRole, setSignerRole] = useState('Primary');
  const [attested, setAttested] = useState(false);
  const [busy, setBusy] = useState(false);
  const [successInfo, setSuccessInfo] = useState<{ cartId: string; markedAt: string } | null>(null);
  const [failure, setFailure] = useState<{ code?: string; message: string } | null>(null);

  const canSubmit =
    cartId.trim().length > 0 &&
    qrPayload.trim().length > 0 &&
    signerEmail.trim().length > 0 &&
    attested &&
    !!user;

  const submit = async (): Promise<void> => {
    if (!sessionId || !user) return;
    setBusy(true);
    setFailure(null);
    try {
      const resp = await apiClient.signing.completeWetSign({
        CartId: cartId.trim(),
        SessionId: sessionId,
        QrPayload: qrPayload.trim(),
        SignedAtUtc: new Date().toISOString() as unknown as Parameters<
          typeof apiClient.signing.completeWetSign
        >[0]['SignedAtUtc'],
        SignerEmail: signerEmail.trim(),
        SignerRole: signerRole,
        OfficerCode: user.upn,
      });
      setSuccessInfo({
        cartId: resp.CartId,
        markedAt: resp.MarkedAt as unknown as string,
      });
      await apiClient.retail.events.write({
        eventType: 'signer-signed',
        eventCategory: 'signing',
        severity: 'info',
        actor: user.upn,
        payloadJson: JSON.stringify({
          sessionId,
          cartId: resp.CartId,
          signerRole,
          signerEmail,
          method: 'wet-sign',
        }),
      });
    } catch (err) {
      if (ApiError.isApiError(err)) {
        setFailure({
          code: err.code,
          message: err.message || 'Wet-sign reassembly failed.',
        });
      } else {
        setFailure({ message: err instanceof Error ? err.message : 'Unknown error.' });
      }
    } finally {
      setBusy(false);
    }
  };

  if (successInfo) {
    return (
      <div className="rsc-wet">
        <div className="rsc-banner rsc-banner--ok" role="status">
          <CheckCircle2 size={16} className="rsc-inline-icon" />
          <div>
            Wet-sign page reassembled. Cart <strong>{successInfo.cartId}</strong> signer{' '}
            <strong>{signerRole}</strong> marked signed at{' '}
            {formatLocal(successInfo.markedAt)}.
          </div>
        </div>
        <div className="rsc-footer">
          <Button variant="ghost" onClick={() => navigate('..')}>
            Back to session
          </Button>
          <Button variant="primary" onClick={() => setSuccessInfo(null)}>
            Scan another page
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="rsc-wet">
      <div className="rsc-detail__crumb">
        <Button variant="ghost" startIcon={<ArrowLeft size={14} />} onClick={() => navigate('..')}>
          Back to session
        </Button>
        <h1>Wet-sign completion · {sessionId}</h1>
        <Pill variant="info">
          <FileSignature size={12} style={{ verticalAlign: '-2px', marginRight: 4 }} />
          QR reassembly
        </Pill>
      </div>

      <div className="rsc-banner rsc-banner--info" role="note">
        <AlertTriangle size={14} className="rsc-inline-icon" />
        <div>
          Scan the wet-sign QR marker from the signed paper page. The API reassembles per-doc
          PDFs into <code>ao_Documents/{'{cifId}'}/signed/wet-*.pdf</code>. 422 errors (mismatch,
          expired, unknown session, already complete) surface below with the exact reason code.
        </div>
      </div>

      <WidgetCard title="QR / signer">
        <div className="rsc-field">
          <label htmlFor="wet-cart">Cart ID</label>
          <input
            id="wet-cart"
            type="text"
            value={cartId}
            onChange={(e) => setCartId(e.target.value)}
          />
        </div>
        <div className="rsc-field">
          <label htmlFor="wet-qr">QR payload (scanned)</label>
          <textarea
            id="wet-qr"
            value={qrPayload}
            onChange={(e) => setQrPayload(e.target.value)}
            placeholder='{"cartId":"…","docInstanceId":"…","pageNumber":1,…}'
          />
        </div>
        <div className="rsc-field">
          <label htmlFor="wet-email">Signer email</label>
          <input
            id="wet-email"
            type="email"
            value={signerEmail}
            onChange={(e) => setSignerEmail(e.target.value)}
          />
        </div>
        <div className="rsc-field">
          <label htmlFor="wet-role">Signer role</label>
          <select
            id="wet-role"
            value={signerRole}
            onChange={(e) => setSignerRole(e.target.value)}
          >
            <option>Primary</option>
            <option>Joint1</option>
            <option>Joint2</option>
            <option>Joint3</option>
          </select>
        </div>
      </WidgetCard>

      <WidgetCard title="FSR attestation">
        <label style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={attested}
            onChange={(e) => setAttested(e.target.checked)}
          />
          I witnessed <strong style={{ marginLeft: 4 }}>{signerEmail || 'the signer'}</strong>'s
          signature on the scanned page.
        </label>
      </WidgetCard>

      {failure && (
        <div className="rsc-banner rsc-banner--err" role="alert">
          <AlertTriangle size={14} className="rsc-inline-icon" />
          <div>
            {failure.code && <strong>{failure.code}</strong>}
            {failure.code && <br />}
            {failure.message}
          </div>
        </div>
      )}

      <div className="rsc-footer">
        <Button variant="ghost" onClick={() => navigate('..')}>
          Cancel
        </Button>
        <Button variant="primary" onClick={() => void submit()} disabled={!canSubmit || busy}>
          {busy ? 'Marking…' : 'Complete wet-sign page'}
        </Button>
      </div>
    </div>
  );
}
